import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link2, ShieldCheck, Loader2, Unlink, Copy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PLATFORMS } from "@/components/community/StreamEmbed";

const RESULT_MSG = {
  connected: "Channel connected & verified",
  denied: "Connection cancelled",
  expired: "Link expired — please try again",
  no_channel: "No channel found on that account",
  no_user: "Account could not be read",
  already_linked: "That channel is already linked to another 7 Legendary account",
  failed: "Connection failed — please try again",
};
const inp = "w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

function NimoVerify({ onDone }) {
  const [code, setCode] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => { const r = await api.post("/connect/nimotv/code"); setCode(r.data.code); };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/connect/nimotv/verify", { channel_url: url }); toast.success(RESULT_MSG.connected); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };
  if (!code) return <button onClick={gen} data-testid="connect-nimotv-code" className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 text-white hover:bg-white/15">Get code</button>;
  return (
    <div className="w-full mt-2 space-y-2" data-testid="nimo-verify-box">
      <p className="text-[11px] text-white/50">Put this code in your Nimo TV room title or announcement, then paste your channel link:</p>
      <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }} data-testid="nimo-code" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/40 font-mono text-sm text-[hsl(var(--primary))]">{code} <Copy size={12} /></button>
      <div className="flex gap-2">
        <input className={inp} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.nimo.tv/yourname" data-testid="nimo-url" />
        <button onClick={verify} disabled={busy || !url.trim()} data-testid="nimo-verify-btn" className="px-4 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-bold disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : "Verify"}</button>
      </div>
    </div>
  );
}

export default function ConnectAccounts() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/connect/status").then((r) => setSt(r.data)).catch(() => setSt({ accounts: {}, configured: {} }));
  useEffect(() => {
    load();
    const res = params.get("result");
    if (params.get("connect") && res) {
      (res === "connected" ? toast.success : toast.error)(RESULT_MSG[res] || RESULT_MSG.failed);
      params.delete("connect"); params.delete("result"); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async (p) => {
    setBusy(p);
    try { const r = await api.post(`/connect/${p}/start`); window.location.assign(r.data.authorize_url); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start connection"); setBusy(""); }
  };
  const unlink = async (p) => { await api.delete(`/connect/${p}`); toast.success("Disconnected"); load(); };

  if (!st) return null;
  return (
    <div className="mt-6 pt-5 border-t border-white/5" data-testid="connect-accounts">
      <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2"><Link2 size={15} className="text-[hsl(var(--primary))]" /> Connect Account</h3>
      <p className="text-xs text-white/40 mb-3">Sign in to your streaming platform to prove you own the channel. Verified channels get a badge and auto-detect turns on automatically.</p>
      <div className="grid sm:grid-cols-2 gap-2 items-start">
        {["youtube", "twitch", "kick", "nimotv"].map((p) => {
          const acc = st.accounts[p];
          const ready = st.configured[p];
          return (
            <div key={p} className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`connect-row-${p}`}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PLATFORMS[p]?.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">{PLATFORMS[p]?.label || p}</div>
                <div className="text-[11px] truncate">
                  {acc ? <span className="inline-flex items-center gap-1 text-[hsl(var(--primary))]" data-testid={`connect-verified-${p}`}><ShieldCheck size={12} /> {acc.username}</span>
                    : <span className="text-white/40">{ready ? "Not connected" : "Key pending"}</span>}
                </div>
              </div>
              {acc ? (
                <button onClick={() => unlink(p)} data-testid={`connect-unlink-${p}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-white/60 hover:text-red-400 border border-white/10"><Unlink size={12} /> Disconnect</button>
              ) : p === "nimotv" ? <NimoVerify onDone={load} /> : (
                <button onClick={() => connect(p)} disabled={!ready || busy === p} data-testid={`connect-btn-${p}`}
                  className="px-3 py-1.5 rounded-full text-xs font-bold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] disabled:opacity-40">
                  {busy === p ? <Loader2 size={13} className="animate-spin" /> : "Connect"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
